import { useState, useEffect } from 'react';
import axios from 'axios';
import { ListChecks, Plus, Pencil, Trash2, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QtyChangeReasonsConfig = () => {
  const [reasons, setReasons] = useState([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, reason: null });
  const [newReason, setNewReason] = useState({ name: '', description: '' });
  const [editData, setEditData] = useState({ name: '', description: '' });

  useEffect(() => { fetchReasons(); }, []);

  const fetchReasons = async () => {
    try {
      const res = await axios.get(`${API}/qty-change-reasons`);
      setReasons(res.data);
    } catch { /* ignore */ }
  };

  const handleCreate = async () => {
    if (!newReason.name) { toast.error('Reason name required'); return; }
    try {
      await axios.post(`${API}/qty-change-reasons`, newReason);
      toast.success('Qty change reason created');
      setNewReason({ name: '', description: '' });
      setCreateDialog(false);
      fetchReasons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (reason) => {
    setEditDialog({ open: true, reason });
    setEditData({ name: reason.name, description: reason.description || '' });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/qty-change-reasons/${editDialog.reason.id}`, editData);
      toast.success('Updated');
      setEditDialog({ open: false, reason: null });
      fetchReasons();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (reason) => {
    if (!window.confirm(`Delete reason "${reason.name}"?`)) return;
    try {
      await axios.delete(`${API}/qty-change-reasons/${reason.id}`);
      toast.success('Deleted');
      fetchReasons();
    } catch { toast.error('Failed'); }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await axios.post(`${API}/qty-change-reasons/seed-defaults`);
      toast.success(res.data.message);
      fetchReasons();
    } catch { toast.error('Failed'); }
  };

  return (
    <Card data-testid="qty-change-reasons-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-blue-600" />
            <div>
              <CardTitle>Qty Change Reasons</CardTitle>
              <CardDescription>Configure reasons for production plan qty changes</CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSeedDefaults}>Seed Defaults</Button>
            <Button size="sm" onClick={() => setCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-qty-reason-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Reason
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {reasons.length === 0 ? (
          <p className="text-center py-6 text-slate-500">No qty change reasons. Click "Seed Defaults" to create standard reasons.</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-slate-50">
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Description</th>
                <th className="px-4 py-2 text-left font-medium">Actions</th>
              </tr></thead>
              <tbody>
                {reasons.map(r => (
                  <tr key={r.id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium">{r.name}</td>
                    <td className="px-4 py-2 text-slate-600">{r.description || '-'}</td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="text-blue-600" onClick={() => handleEdit(r)}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(r)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Qty Change Reason</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="text-sm font-medium mb-2 block">Name *</label><Input value={newReason.name} onChange={e => setNewReason(p => ({ ...p, name: e.target.value }))} placeholder="Material shortage" data-testid="input-qty-reason-name" /></div>
            <div><label className="text-sm font-medium mb-2 block">Description</label><Input value={newReason.description} onChange={e => setNewReason(p => ({ ...p, description: e.target.value }))} placeholder="Insufficient raw material" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="create-qty-reason-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialog.open} onOpenChange={open => !open && setEditDialog({ open: false, reason: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Qty Change Reason</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="text-sm font-medium mb-2 block">Name *</label><Input value={editData.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} /></div>
            <div><label className="text-sm font-medium mb-2 block">Description</label><Input value={editData.description} onChange={e => setEditData(p => ({ ...p, description: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, reason: null })}>Cancel</Button>
            <Button onClick={handleSaveEdit}><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default QtyChangeReasonsConfig;
